import React, { useState } from 'react';
import { getShops } from '../../../model/shop';
import { useData, usePaginatedResponse } from '../../../model/data';
import { Icon } from '../../../components/Icon/Icon';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { Pagination } from '../../../components/Pagination/Pagination';
import { SortableList } from '../../../components/SortableList/SortableList';
import { serializeQuery } from '../../../model/filter';
import { shopPath } from '../../../routes';
import { useMeta } from '../../../model/meta';
import { ShopForm } from './ShopCard/ShopForm';
import { useQueryAsState } from '../../../model/history';
import { Tag } from '../../../components/Tag/Tag';
import { FilterBar } from '../../../components/FilterBar/FilterBar';
import { t } from '../../../locales/translate';
import { compact } from 'lodash';
import { paymentMethodsById } from '../../../model/payment';
import { SortByIcon } from '../../../components/SortableList/SortByIcon';
import { Modal } from '../../../components/Modal/Modal';

export function Shops(): JSX.Element {
  const [params] = useQueryAsState();
  const [showAddModal, setShowAddModal] = useState(false);
  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getShops({
        page,
        perPage,
        term: serializeQuery(params.query),
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      }),
    JSON.stringify(params)
  );

  useMeta({
    title: t.model.shop._plural,
  });

  const shops = useData((db) => compact(paginator.currentIds.map((e) => db.table('shops').find(e))));

  return (
    <Container className="shops">
      <Card
        title={t.model.shop._plural}
        elevation={2}
        actions={
          <Icon
            name="plus"
            onClick={() => {
              setShowAddModal(true);
            }}
          />
        }
        pagination={<Pagination {...paginator} />}
        fullbleed
      >
        <SortableList
          items={shops}
          error={paginator.error}
          actions={<FilterBar filters={['query']} />}
          rowLink={(row) => shopPath(row)}
          loading={paginator.loading}
          columns={[
            {
              label: t.model.user.name,
              apiSortIcon: <SortByIcon sortBy="name" />,
              dataKey: 'name',
            },
            {
              label: t.general.slug,
              dataKey: 'slug',
            },
            {
              label: t.model.shop.defaultLocale,
              dataKey: 'defaultLocale',
            },
            {
              label: t.model.shop.domain,
              dataKey: 'domain',
            },
            {
              label: t.model.shop.paymentBlacklist,
              dataKey: 'paymentBlacklist',
              cellRenderer: (row) =>
                row.paymentBlacklist?.map((payment) => {
                  return (
                    <Tag key={payment} style={{ marginRight: '8px' }} text={paymentMethodsById[payment]?.name}></Tag>
                  );
                }),
            },
            {
              label: t.model.shop.waitingQueue,
              dataKey: 'waitingQueue',
              cellRenderer: (row) => row.waitingQueue && <Icon style={{ color: 'var(--color-online)' }} name="check" />,
            },
            {
              label: t.model.shop.siteMap,
              dataKey: 'sitemap',
              cellRenderer: (row) => row.sitemap && <Icon style={{ color: 'var(--color-online)' }} name="check" />,
            },
          ]}
          fullBleed
        />
      </Card>
      <Modal open={showAddModal} title={t.actions.add(t.model.shop._name)} size="small">
        <ShopForm shop={{}} onClose={() => setShowAddModal(false)} />
      </Modal>
    </Container>
  );
}
